// P9/S7 — caller-identity hook for the support features (helpdesk, spec 70).
//
// GET /v1/me resolves the caller's own employee_id (drives "my ticket" checks)
// and roles (drives the agent-only affordances: internal-note toggle,
// transition controls, article manage/publish). Mirrors the recognition
// feature's use-me-identity precedent — kept feature-local so support doesn't
// import across feature boundaries.
//
// ADVISORY ONLY (ADR-008/ADR-017): these flags decide what the UI SHOWS —
// the server independently enforces the agent boundary (SUPPORT_AGENT_ROLES
// in-handler check + the helpdesk matrix cell). A forged/stale flag dangles a
// button that then 403s; it never widens authority.

import { useQuery } from "@tanstack/react-query";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

/** The slice of /v1/me the support features read. */
export interface MeIdentity {
  readonly employee_id: string | null;
  readonly roles: readonly string[];
}

/** Mirror of the S5 route's SUPPORT_AGENT_ROLES (spec 70 default_assignments
 *  for perm_support_ticket_manage / perm_support_article_manage —
 *  role_support_agent rides as a JWT realm-role at MVP). */
export const SUPPORT_AGENT_ROLES: ReadonlySet<string> = new Set([
  "role_super_admin",
  "role_hr_admin",
  "role_support_agent",
]);

/** True iff any of the caller's roles is a support-agent role. */
export function isSupportAgent(roles: readonly string[]): boolean {
  return roles.some((r) => SUPPORT_AGENT_ROLES.has(r));
}

/** ScopeGate capability strings for the support surfaces (UI vocabulary). */
export const SUPPORT_CAP_AGENT = "support.agent";
export const SUPPORT_CAP_ARTICLE_MANAGE = "support.article_manage";

/** The support capability set the caller's roles expose in the UI. */
export function supportCapabilities(roles: readonly string[]): ReadonlySet<string> {
  const caps = new Set<string>();
  if (isSupportAgent(roles)) {
    caps.add(SUPPORT_CAP_AGENT);
    caps.add(SUPPORT_CAP_ARTICLE_MANAGE);
  }
  return caps;
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeIdentity>>;

async function fetchMe(): Promise<MeIdentity> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { employee_id: data.employee_id, roles: data.roles };
}

export interface SupportIdentityState {
  readonly employeeId: string | null;
  readonly roles: readonly string[];
  /** Agent boundary flag — default-closed while loading. */
  readonly isAgent: boolean;
  /** ScopeGate `allowed` set derived from the resolved roles. */
  readonly capabilities: ReadonlySet<string>;
  readonly isLoading: boolean;
}

/**
 * Resolve the caller's own employee id + support-agent capability. While
 * loading the agent flag is FALSE (default-closed — agent affordances stay
 * hidden until the real identity resolves, never flash).
 */
export function useSupportIdentity(): SupportIdentityState {
  const query = useQuery({
    queryKey: ["me", "identity"],
    queryFn: fetchMe,
    staleTime: 60_000,
  });
  const roles = query.data?.roles ?? [];
  return {
    employeeId: query.data?.employee_id ?? null,
    roles,
    isAgent: isSupportAgent(roles),
    capabilities: supportCapabilities(roles),
    isLoading: query.isLoading,
  };
}
